import React, { useState } from 'react';
import { Modal, Form, Input, Typography, Tabs, Card, Tooltip, Button, Space } from 'antd';
import { InfoCircleOutlined, DeleteOutlined } from '@ant-design/icons';

const { TextArea } = Input;
const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;

interface AgentsCompanyModalProps {
  open: boolean;
  onClose: () => void;
  formData: {
    bot_id: string;
    dsid: string;
    kbid: string;
    supervisor: {
      agentName: string;
      agent_id: string;
      instruction: string;
      modified?: boolean;
    };
    multi_agents: Array<{
      agentName: string;
      agent_id: string;
      instruction: string;
      collaboration_instruction: string;
      modified?: boolean;
      toDelete?: boolean;
    }>;
    modified?: boolean;
  };
  handleChange: (agentId: string, value: string, isSupervisor?: boolean, isCollaboration?: boolean) => void;
  handleUpdate: (data: any) => void;
  loading: boolean;
}

const tooltipStyles = {
  text: {
    color: '#ffffff',
    margin: 0
  },
  list: {
    color: '#ffffff',
    margin: '8px 0',
    paddingLeft: 16
  },
  container: {
    maxWidth: 300
  }
};

const AgentsCompanyModal: React.FC<AgentsCompanyModalProps> = ({
  open,
  onClose,
  formData,
  handleChange: parentHandleChange,
  handleUpdate: parentHandleUpdate,
  loading
}) => {
  const [modifiedFields, setModifiedFields] = useState<{
    supervisor: boolean;
    multiAgents: { [key: string]: boolean };
  }>({
    supervisor: false,
    multiAgents: {}
  });

  const [localFormData, setLocalFormData] = useState(formData);

  // Reset modifications when modal opens/closes
  React.useEffect(() => {
    setModifiedFields({ supervisor: false, multiAgents: {} });
    setLocalFormData(formData);
  }, [open, formData]);

  const handleChange = (agentId: string, value: string, isSupervisor?: boolean, isCollaboration?: boolean) => {
    if (isSupervisor) {
      setLocalFormData(prev => ({
        ...prev,
        supervisor: {
          ...prev.supervisor,
          instruction: value,
          modified: true
        }
      }));
      setModifiedFields(prev => ({ ...prev, supervisor: true }));
    } else {
      setLocalFormData(prev => ({
        ...prev,
        multi_agents: prev.multi_agents.map(agent => 
          agent.agent_id === agentId 
            ? { 
                ...agent, 
                [isCollaboration ? 'collaboration_instruction' : 'instruction']: value,
                modified: true 
              }
            : agent
        )
      }));
      setModifiedFields(prev => ({
        ...prev,
        multiAgents: {
          ...prev.multiAgents,
          [agentId]: true
        }
      }));
    }
  };

  const handleUpdate = () => {
    const updatedData = {
      ...localFormData,
      bot_id: formData.bot_id,
      dsid: formData.dsid,
      kbid: formData.kbid,
      modified: modifiedFields.supervisor || Object.values(modifiedFields.multiAgents).some(value => value),
      supervisor: {
        ...localFormData.supervisor,
        modified: modifiedFields.supervisor
      },
      multi_agents: localFormData.multi_agents.map(agent => ({
        ...agent,
        modified: !!modifiedFields.multiAgents[agent.agent_id]
      }))
    };

    parentHandleUpdate(updatedData);
  };

  const handleDeleteAgent = (agentId: string) => {
    setLocalFormData(prev => ({
      ...prev,
      multi_agents: prev.multi_agents.map(agent => 
        agent.agent_id === agentId 
          ? { ...agent, toDelete: true, modified: true }
          : agent
      ),
      modified: true
    }));
    setModifiedFields(prev => ({
      ...prev,
      multiAgents: {
        ...prev.multiAgents,
        [agentId]: true
      }
    }));
  };

  const handleRestoreAgent = (agentId: string) => {
    setLocalFormData(prev => ({
      ...prev,
      multi_agents: prev.multi_agents.map(agent => 
        agent.agent_id === agentId 
          ? { ...agent, toDelete: false, modified: true }
          : agent
      ),
      modified: true
    }));
    setModifiedFields(prev => ({
      ...prev,
      multiAgents: {
        ...prev.multiAgents,
        [agentId]: true
      }
    }));
  };

  // Actualizar el estado local cuando cambian los datos externos
  React.useEffect(() => {
    setLocalFormData(formData);
    
    // Verificar si hay cambios comparando las instrucciones
    const supervisorModified = formData.supervisor?.instruction !== localFormData?.supervisor?.instruction;
    const agentModifications: { [key: string]: boolean } = {};
    
    formData.multi_agents?.forEach(agent => {
      const localAgent = localFormData?.multi_agents?.find(a => a.agent_id === agent.agent_id);
      agentModifications[agent.agent_id] = agent.instruction !== localAgent?.instruction;
    });

    setModifiedFields({
      supervisor: supervisorModified,
      multiAgents: agentModifications
    });
  }, [formData]);

  const supervisorTooltip = (
    <div style={tooltipStyles.container}>
      <Paragraph style={tooltipStyles.text}>
        Aquí puedes configurar las instrucciones principales del supervisor. Este agente:
      </Paragraph>
      <ul style={tooltipStyles.list}>
        <li>Coordina las interacciones entre todos los agentes</li>
        <li>Maneja el flujo inicial de conversación</li>
        <li>Determina qué agente especializado debe responder</li>
      </ul>
      <Paragraph style={tooltipStyles.text}>
        Las instrucciones deben ser claras y específicas sobre cómo manejar diferentes tipos de consultas.
      </Paragraph>
    </div>
  );

  const agentTooltip = (
    <div style={tooltipStyles.container}>
      <Paragraph style={tooltipStyles.text}>
        Configura las instrucciones específicas para cada agente. Considera:
      </Paragraph>
      <ul style={tooltipStyles.list}>
        <li>El rol específico del agente</li>
        <li>Tipos de consultas que debe manejar</li>
        <li>Tono y estilo de respuesta</li>
        <li>Acciones específicas que puede realizar</li>
      </ul>
      <Paragraph style={tooltipStyles.text}>
        Cuanto más específicas sean las instrucciones, mejor será la respuesta del agente.
      </Paragraph>
    </div>
  );

  return (
    <Modal
      title={<Title level={4}>Editar Instrucciones de Agentes</Title>}
      open={open}
      onCancel={onClose}
      onOk={handleUpdate}
      confirmLoading={loading}
      width={1200}
      style={{ top: 20 }}
    >
      <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
        <Tabs defaultActiveKey="info">
          <TabPane tab="Información General" key="info">
            <Card>
              <Form layout="vertical">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <Form.Item label="ID del Bot">
                    <Input value={localFormData.bot_id} disabled />
                  </Form.Item>
                  <Form.Item label="ID del Conjunto de Datos">
                    <Input value={localFormData.dsid} disabled />
                  </Form.Item>
                  <Form.Item label="ID de Base de Conocimientos">
                    <Input value={localFormData.kbid} disabled />
                  </Form.Item>
                </div>
              </Form>
            </Card>
          </TabPane>

          <TabPane tab="Supervisor" key="supervisor">
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>Información del Supervisor</span>
                  {modifiedFields.supervisor && (
                    <Text type="warning">(Modificado)</Text>
                  )}
                </div>
              }
              extra={
                <Tooltip title={supervisorTooltip} overlayInnerStyle={{ backgroundColor: '#1f1f1f' }}>
                  <Button type="text" icon={<InfoCircleOutlined />}>
                    Guía de Configuración
                  </Button>
                </Tooltip>
              }
            >
              <Form layout="vertical">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <Form.Item label="Nombre del Supervisor">
                    <Input value={localFormData.supervisor?.agentName} disabled />
                  </Form.Item>
                  <Form.Item label="ID del Supervisor">
                    <Input value={localFormData.supervisor?.agent_id} disabled />
                  </Form.Item>
                </div>
                <Form.Item 
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Instrucciones del Supervisor
                      <Tooltip 
                        title="Configura cómo el supervisor debe coordinar y dirigir las interacciones"
                        overlayInnerStyle={{ backgroundColor: '#1f1f1f', color: '#ffffff' }}
                      >
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </div>
                  }
                >
                  <TextArea
                    rows={8}
                    value={localFormData.supervisor?.instruction}
                    onChange={(e) => handleChange(localFormData.supervisor.agent_id, e.target.value, true)}
                    placeholder="Ingrese las instrucciones detalladas para el supervisor, incluyendo cómo debe manejar diferentes tipos de consultas y coordinar con otros agentes"
                  />
                </Form.Item>
              </Form>
            </Card>
          </TabPane>

          <TabPane tab="Agentes" key="agents">
            <Card 
              style={{ marginBottom: '16px' }}
              type="inner"
              extra={
                <Tooltip title={agentTooltip} overlayInnerStyle={{ backgroundColor: '#1f1f1f' }}>
                  <Button type="text" icon={<InfoCircleOutlined />}>
                    Guía de Configuración
                  </Button>
                </Tooltip>
              }
            >
              <Paragraph>
                Configure las instrucciones específicas para cada agente. Las instrucciones bien definidas ayudarán a que cada agente:
              </Paragraph>
              <ul>
                <li>Entienda su rol específico</li>
                <li>Proporcione respuestas precisas y relevantes</li>
                <li>Mantenga un tono y estilo consistente</li>
                <li>Sepa cuándo y cómo interactuar con otros agentes</li>
              </ul>
            </Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {localFormData.multi_agents?.map((agent, index) => (
                <Card 
                  key={agent.agent_id}
                  title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>Agente {index + 1}</span>
                      {modifiedFields.multiAgents[agent.agent_id] && (
                        <Text type="warning">(Modificado)</Text>
                      )}
                      {agent.toDelete && (
                        <Text type="danger">(Marcado para eliminar)</Text>
                      )}
                    </div>
                  }
                  style={{ 
                    marginBottom: '8px',
                    opacity: agent.toDelete ? 0.7 : 1,
                    background: agent.toDelete ? '#fff1f0' : undefined
                  }}
                  extra={
                    <Space>
                      <Tooltip 
                        title={agent.toDelete ? "Restaurar agente" : "Marcar para eliminar"} 
                        overlayInnerStyle={{ backgroundColor: '#1f1f1f' }}
                      >
                        <Button 
                          type="text" 
                          danger={!agent.toDelete}
                          icon={agent.toDelete ? <InfoCircleOutlined /> : <DeleteOutlined />}
                          onClick={() => agent.toDelete ? handleRestoreAgent(agent.agent_id) : handleDeleteAgent(agent.agent_id)}
                          disabled={loading}
                        >
                          {agent.toDelete ? "Restaurar" : "Eliminar"}
                        </Button>
                      </Tooltip>
                    </Space>
                  }
                >
                  <Form layout="vertical">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      <Form.Item label="Nombre del Agente">
                        <Input value={agent.agentName} disabled />
                      </Form.Item>
                      <Form.Item label="ID del Agente">
                        <Input value={agent.agent_id} disabled />
                      </Form.Item>
                    </div>
                    <Form.Item 
                      label={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          Instrucciones
                          <Tooltip 
                            title="Define el comportamiento específico de este agente"
                            overlayInnerStyle={{ backgroundColor: '#1f1f1f', color: '#ffffff' }}
                          >
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </div>
                      }
                    >
                      <TextArea
                        rows={6}
                        value={agent.instruction}
                        onChange={(e) => handleChange(agent.agent_id, e.target.value)}
                        placeholder="Defina las instrucciones específicas para este agente, incluyendo su rol, tipos de consultas que maneja y cómo debe responder"
                        disabled={agent.toDelete || loading}
                      />
                    </Form.Item>
                    <Form.Item 
                      label={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          Instrucciones de Colaboración
                          <Tooltip 
                            title="Define cómo este agente debe colaborar con el supervisor y otros agentes"
                            overlayInnerStyle={{ backgroundColor: '#1f1f1f', color: '#ffffff' }}
                          >
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                          </Tooltip>
                        </div>
                      }
                    >
                      <TextArea
                        rows={6}
                        value={agent.collaboration_instruction}
                        onChange={(e) => handleChange(agent.agent_id, e.target.value, false, true)}
                        placeholder="Defina las instrucciones de colaboración entre este agente y el supervisor"
                        disabled={agent.toDelete || loading}
                      />
                    </Form.Item>
                  </Form>
                </Card>
              ))}
            </div>
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );
};

export default AgentsCompanyModal; 